import React, { useEffect, useMemo, useState } from 'react';
import { pl as apiPl, sig as apiSig } from '../services/api';
import { useFiltresVentes, usePanneau } from './ventesUtils';
import { sectionsGroupes, basculerGroupe, nomDuPerimetre } from './regroupements';
import FiltresPanel from './FiltresPanel';
import './Ventes.css';
import './VentesJour.css';
import './Pl.css';

// ─── Finance — P&L (2026-10-28) ─────────────────────────────────────────────
// Le « P&L de taille » des classeurs Excel : mêmes lignes, mêmes sous-totaux,
// mêmes colonnes (réalisé N-1, budget, réalisé N, écarts), en k€ à une
// décimale. Calcul côté serveur (services/pl.js) ; l'écran fait les écarts.
//
// Gestes (demande du user : « extrêmement intuitif et interactif ») :
//   · en-tête de carte : activité (Total, VN, VO, APV, PR, Admin) et lecture
//     (Mois, Cumul, Mensuel AAAA = un mois par colonne) — portés par l'URL ;
//   · ▸ sur un sous-total : replie / déplie ses postes ;
//   · clic sur un montant : la modale du détail — par fournisseur pour une
//     charge, par client pour un produit (particuliers regroupés, jamais
//     nommés), par compte, et écriture par écriture, comparé à la référence.
// Filtres du panneau : le mois d'arrêté et les sociétés.

const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const moisLong = (ym) => (ym ? `${MOIS_FR[Number(ym.slice(5)) - 1]} ${ym.slice(0, 4)}` : '');
const moisCourt = (ym) => { const l = MOIS_FR[Number(ym.slice(5)) - 1]; return l.length > 5 ? `${l.slice(0, 4)}.` : l; };
const ACT_LIB = { '': 'Total', VN: 'VN', VO: 'VO', APV: 'APV', PR: 'PR', ADMIN: 'Admin' };
const fr = (v, opt) => v.toLocaleString('fr-FR', opt);
// Formats (demande du user, 2026-10-28) : montants SANS décimale (k€ ou €),
// taux et variations à une décimale.
// Arrondi AVANT formatage : −300 € donnait « −0 » (le zéro négatif garde son signe).
const arr = (x, d = 0) => { const r = Math.round(x * 10 ** d) / 10 ** d; return r === 0 ? 0 : r; };
const k1 = (v) => (v == null ? '—' : fr(arr(v / 1000), { maximumFractionDigits: 0 }));
const k1s = (v) => (v == null ? '—' : fr(arr(v / 1000), { signDisplay: 'exceptZero', maximumFractionDigits: 0 }));
const eur = (v) => (v == null ? '—' : `${fr(arr(v), { maximumFractionDigits: 0 })} €`);
const pctV = (v) => (v == null ? '—' : `${fr(arr(v * 100, 1), { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`);
const ratio = (v) => (v == null ? '—' : fr(arr(v, 1), { minimumFractionDigits: 1, maximumFractionDigits: 1 }));
// Deux modèles, un seul écran (2026-10-29) : le P&L auto et les SIG partagent
// le moteur (services/pl.js) et cet écran ; chacun apporte son titre, ses
// lignes de total (graisse forte + filet), ses tuiles et ses lectures.
const MODELES = {
  pl: {
    api: apiPl, titre: 'P&L', ca: 'ca_ht', activites: true, lectures: ['mensuel', 'concessions', 'societes', 'marques'],
    totaux: new Set(['ca_ht', 'marge_brute', 'marge_nette', 'total_produits', 'rex', 'rcai']),
    kpis: [['ca_ht'], ['marge_nette', 'taux_mn'], ['rex', 'rex_pct'], ['rcai', 'rcai_pct', 'RCAI']],
  },
  sig: {
    api: apiSig, titre: 'SIG', ca: 'ca', activites: false, lectures: ['mensuel', 'concessions', 'societes'],
    totaux: new Set(['ca', 'marge_commerciale', 'valeur_ajoutee', 'ebe', 'resultat_exploitation', 'rcai', 'resultat']),
    kpis: [['valeur_ajoutee', 'taux_va'], ['ebe', 'taux_ebe', 'EBE'], ['resultat_exploitation', 'taux_rex'], ['rcai', 'taux_rcai']],
  },
};
// Variation rapportée à la valeur ABSOLUE de la référence (Synthèse du
// classeur) : une charge qui passe de −100 à −120 fait −20 %, défavorable.
const ecart = (v, ref) => (v == null || ref == null ? null : v - ref);
const varPct = (v, ref) => (v == null || !ref ? null : (v - ref) / Math.abs(ref));
const cl = (x) => (x == null || Math.abs(x) < 1e-9 ? '' : x > 0 ? 'tbl-pos' : 'tbl-neg');
const pctS = (p) => (p == null ? '—' : `${fr(arr(p * 100, 1), { signDisplay: 'exceptZero', minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`);
// Détail d'une cellule : colonnes de comparaison (N-1 / budget, écart, %).
// Masquées le 2026-10-30 tant que 2025 n'est pas importé (demande du user).
const COMPARAISON_DETAIL = false;
const valeur = (l, v) => (l.type === 'taux' ? (l.ratio ? ratio(v) : pctV(v)) : k1(v));

// ─── Modale du détail d'une cellule ─────────────────────────────────────────
function ModaleDetail({ cible, contexte, onFermer, api }) {
  const [d, setD] = useState(null);
  const [erreur, setErreur] = useState('');
  // « Par compte » d'abord (demande du user, 2026-10-28).
  const [onglet, setOnglet] = useState('comptes');
  const [cherche, setCherche] = useState('');
  // Filtres croisés, comme le bloc Stock par marque : un tiers sélectionné
  // filtre les comptes et les écritures ; un compte filtre les tiers et les
  // écritures ; les deux se combinent ; recliquer la ligne annule.
  const [selT, setSelT] = useState(null);
  const [selC, setSelC] = useState(null);
  const [selM, setSelM] = useState(null);  // marque (« ∅ » = sans marque)
  const [selS, setSelS] = useState(null);  // concession (cellule qui en couvre plusieurs)
  const [ecr, setEcr] = useState(null);   // { liste, nb } — relues filtrées côté serveur
  useEffect(() => {
    let actif = true; setD(null); setErreur(''); setSelT(null); setSelC(null); setSelM(null); setSelS(null);
    api.detail({ ...contexte, ligne: cible.ligne, colonne: cible.colonne })
      .then((r) => { if (actif) { setD(r.data); setEcr({ liste: r.data.ecritures || [], nb: r.data.nbEcritures || 0 }); } })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Détail indisponible'); });
    return () => { actif = false; };
  }, [cible, contexte, api]);
  // Écritures : plafonnées aux 500 plus fortes, donc relues avec les filtres
  // (celles d'un petit fournisseur manqueraient dans la liste non filtrée).
  useEffect(() => {
    if (!d) return undefined;
    let actif = true; setEcr((x) => ({ ...(x || {}), charge: true }));
    api.detail({ ...contexte, ligne: cible.ligne, colonne: cible.colonne, f_tiers: selT ?? undefined, f_compte: selC || undefined, f_marque: selM || undefined, f_conc: selS || undefined })
      .then((r) => { if (actif) setEcr({ liste: r.data.ecritures || [], nb: r.data.nbEcritures || 0 }); })
      .catch(() => { if (actif) setEcr({ liste: [], nb: 0 }); });
    return () => { actif = false; };
  }, [selT, selC, selM, selS]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const k = (ev) => { if (ev.key === 'Escape') onFermer(); };
    window.addEventListener('keydown', k); return () => window.removeEventListener('keydown', k);
  }, [onFermer]);

  const nature = d?.nature || 'tiers';
  const NOM_TIERS = { fournisseur: 'Fournisseur', client: 'Client', tiers: 'Fournisseur / client' }[nature];
  const ONGLET_TIERS = { fournisseur: 'Par fournisseur', client: 'Par client', tiers: 'Par fournisseur / client' }[nature];
  const q = cherche.trim().toLowerCase();
  // Agrégats depuis le tableau croisé, chacun filtré par la sélection de l'autre.
  const agreger = (cle, filtre) => {
    const m = new Map();
    (d?.croise || []).filter(filtre).forEach((r) => {
      const k = cle(r); const x = m.get(k) || { a: 0, b: 0, aProv: 0 };
      x.a += r.a; x.b += r.b; x.aProv += r.aProv; m.set(k, x);
    });
    return m;
  };
  const metaT = new Map((d?.tiers || []).map((t) => [t.cle, t]));
  const metaC = new Map((d?.comptes || []).map((c) => [c.compte, c]));
  const net = (x) => Math.abs(x.a) >= 0.5 || Math.abs(x.b) >= 0.5;
  const okC = (r) => !selC || r.c === selC; const okT = (r) => selT == null || r.t === selT; const okM = (r) => !selM || r.m === selM;
  const okS = (r) => !selS || r.s === selS;
  const tiers = [...agreger((r) => r.t, (r) => okC(r) && okM(r) && okS(r)).entries()]
    .map(([cle, x]) => ({ ...(metaT.get(cle) || { nom: cle }), cle, ...x })).filter(net)
    .filter((t) => !q || t.nom.toLowerCase().includes(q)).sort((a, b) => Math.abs(b.a) - Math.abs(a.a) || Math.abs(b.b) - Math.abs(a.b));
  const comptes = [...agreger((r) => r.c, (r) => okT(r) && okM(r) && okS(r)).entries()]
    .map(([compte, x]) => ({ ...(metaC.get(compte) || {}), compte, ...x })).filter(net)
    .filter((c) => !q || `${c.compte} ${c.libelle || ''}`.toLowerCase().includes(q)).sort((a, b) => Math.abs(b.a) - Math.abs(a.a));
  // Par marque (2026-10-28, demande du user) : affiché seulement si la cellule
  // porte au moins une marque (4e segment de la section analytique).
  const libM = (m) => (m === '∅' ? 'Sans marque' : m);
  const avecMarques = (d?.marques || []).some((m) => m !== '∅');
  const marques = [...agreger((r) => r.m, (r) => okT(r) && okC(r) && okS(r)).entries()]
    .map(([m, x]) => ({ m, nom: libM(m), ...x })).filter(net)
    .filter((x) => !q || x.nom.toLowerCase().includes(q)).sort((a, b) => Math.abs(b.a) - Math.abs(a.a));
  // Par concession (2026-10-29, demande du user) : sur une cellule qui couvre
  // plusieurs concessions (colonne Total), pour les comparer d'un coup d'œil.
  const avecConc = (d?.concessions || []).length > 1;
  const concs = [...agreger((r) => r.s, (r) => okT(r) && okC(r) && okM(r)).entries()]
    .map(([sc, x]) => ({ s: sc, ...x })).filter(net)
    .filter((x) => !q || x.s.toLowerCase().includes(q)).sort((a, b) => Math.abs(b.a) - Math.abs(a.a));
  const ecritures = (ecr?.liste || []).filter((x) => !q || `${x.tiers || ''} ${x.compte} ${x.libelle_compte || ''} ${x.piece || ''}`.toLowerCase().includes(q));
  const e = d ? ecart(d.a.total, d.b.total) : null;
  // Facturé (lots Réel) / provisionné (lots Simulation, journal 9600) : sur un réalisé seulement.
  const avecProv = !!d?.a?.reel;
  // Période de référence non importée : « — », jamais un écart égal au montant.
  const bOk = d?.b?.disponible !== false;
  // Modale allégée (2026-10-30, demande du user : « trop complexe, tout doit
  // tenir à l'écran ») : ni période de comparaison, ni écart, ni part, tant que
  // 2025 n'est pas importé. Le serveur calcule toujours la référence (d.b) :
  // repasser à true suffit à la rétablir.
  const avecRef = COMPARAISON_DETAIL;
  const filtre = selT != null || !!selC || !!selM || !!selS;
  const Ligne = ({ lib, sous, a, b, prov, actif, onClick, titre }) => (
    <tr className={`tbl-clic${actif ? ' actif' : ''}`} onClick={onClick} title={titre}>
      <td className="l">{lib}{sous ? <small className="pl-m-sous">{sous}</small> : null}</td>
      <td className="pl-n">{k1(a)}</td>
      {avecProv && <><td>{k1(a - (prov || 0))}</td><td className={prov ? 'pl-m-prov' : ''}>{k1(prov || 0)}</td></>}
      {avecRef && <><td>{bOk ? k1(b) : '—'}</td>
        <td className={bOk ? cl(ecart(a, b)) : ''}>{bOk ? k1s(ecart(a, b)) : '—'}</td><td className={bOk ? cl(varPct(a, b)) : ''}>{bOk ? pctS(varPct(a, b)) : '—'}</td></>}
    </tr>
  );
  const Tete = ({ premiere }) => (
    <thead><tr><th className="l">{premiere}</th><th className="pl-n">{d.a.label}</th>
      {avecProv && <><th title="Écritures comptabilisées (lots Réel)">dont facturé</th><th title="Provisions de fin de mois (lots Simulation, journal 9600)">dont provisionné</th></>}
      {avecRef && <><th>{d.b.label}</th><th>Écart</th><th>%</th></>}</tr></thead>
  );
  // Pied : le total des lignes AFFICHÉES (il suit les filtres).
  const Pied = ({ rows }) => {
    const a = rows.reduce((t, x) => t + x.a, 0); const b = rows.reduce((t, x) => t + x.b, 0); const p = rows.reduce((t, x) => t + x.aProv, 0);
    return (
      <tfoot><tr><td className="l">Total{filtre || q ? ' affiché' : ''}</td><td className="pl-n">{k1(a)}</td>
        {avecProv && <><td>{k1(a - p)}</td><td>{k1(p)}</td></>}
        {avecRef && <><td>{bOk ? k1(b) : '—'}</td>
          <td className={bOk ? cl(ecart(a, b)) : ''}>{bOk ? k1s(ecart(a, b)) : '—'}</td><td className={bOk ? cl(varPct(a, b)) : ''}>{bOk ? pctS(varPct(a, b)) : '—'}</td></>}</tr></tfoot>
    );
  };
  const tSel = selT != null ? metaT.get(selT) : null; const cSel = selC ? metaC.get(selC) : null;

  return (
    <div className="pl-m-ov" onClick={onFermer}>
      <div className="pl-m" role="dialog" aria-label={`Détail — ${cible.libelle}`} onClick={(ev) => ev.stopPropagation()}>
        <div className="pl-m-h">
          <div>
            <h2>{cible.libelle}</h2>
            <p>{[contexte.activite ? `Activité ${ACT_LIB[contexte.activite]}` : 'Toutes activités', cible.periode, cible.perimetre].filter(Boolean).join(' · ')}</p>
          </div>
          <button type="button" className="pl-m-x" onClick={onFermer} aria-label="Fermer">✕</button>
        </div>
        {erreur ? <div className="pl-m-vide">{erreur}</div> : !d ? <div className="pl-m-vide">Chargement…</div> : d.vide || (!d.nbEcritures && !d.tiers.length) ? <div className="pl-m-vide">Aucune écriture sur cette cellule, ni sur la période de comparaison.</div> : (
          <>
            <div className={`pl-m-kpis${avecRef ? '' : ' pl-m-kpis2'}`}>
              <div><span>{d.a.label}</span><b>{k1(d.a.total)} k€</b><small>{avecProv && d.a.prov ? `facturé ${k1(d.a.total - d.a.prov)} · provisionné ${k1(d.a.prov)} k€` : eur(d.a.total)}</small></div>
              {avecRef && <><div><span>{d.b.label}</span><b>{bOk ? `${k1(d.b.total)} k€` : '—'}</b><small>{bOk ? eur(d.b.total) : 'non importé'}</small></div>
              <div><span>Écart</span><b className={bOk ? cl(e) : ''}>{bOk ? `${k1s(e)} k€` : '—'}</b><small className={bOk ? cl(varPct(d.a.total, d.b.total)) : ''}>{bOk ? pctS(varPct(d.a.total, d.b.total)) : 'pas de référence'}</small></div></>}
              <div><span>{nature === 'client' ? 'Clients' : nature === 'fournisseur' ? 'Fournisseurs' : 'Tiers'}</span><b>{d.tiers.length}</b><small>{d.nbEcritures} écriture{d.nbEcritures > 1 ? 's' : ''} · {d.comptes.length} compte{d.comptes.length > 1 ? 's' : ''}</small></div>
            </div>
            <div className="pl-m-outils">
              <span className="st-seg">
                <button type="button" className={onglet === 'comptes' ? 'on' : ''} onClick={() => setOnglet('comptes')}>Par compte</button>
                <button type="button" className={onglet === 'tiers' ? 'on' : ''} onClick={() => setOnglet('tiers')}>{ONGLET_TIERS}</button>
                {avecConc && <button type="button" className={onglet === 'concessions' ? 'on' : ''} onClick={() => setOnglet('concessions')}>Par concession</button>}
                {avecMarques && <button type="button" className={onglet === 'marques' ? 'on' : ''} onClick={() => setOnglet('marques')}>Par marque</button>}
                <button type="button" className={onglet === 'ecr' ? 'on' : ''} onClick={() => setOnglet('ecr')}>Écritures</button>
              </span>
              {selS && <button type="button" className="pl-m-puce" onClick={() => setSelS(null)} title="Retirer ce filtre">Concession : {selS} ✕</button>}
              {selM && <button type="button" className="pl-m-puce" onClick={() => setSelM(null)} title="Retirer ce filtre">Marque : {libM(selM)} ✕</button>}
              {cSel && <button type="button" className="pl-m-puce" onClick={() => setSelC(null)} title="Retirer ce filtre">Compte : {cSel.compte} {cSel.libelle ? `· ${cSel.libelle}` : ''} ✕</button>}
              {tSel && <button type="button" className="pl-m-puce" onClick={() => setSelT(null)} title="Retirer ce filtre">{NOM_TIERS} : {tSel.nom} ✕</button>}
              <input className="pl-m-cherche" type="search" placeholder="🔍 Rechercher…" value={cherche} onChange={(ev) => setCherche(ev.target.value)} />
            </div>
            <div className="pl-m-b">
              {onglet === 'comptes' && (
                <table className="vt-tbl tbl-dense pl-m-tbl">
                  <Tete premiere="Compte" />
                  <tbody>{comptes.map((c) => (
                    <Ligne key={c.compte} lib={`${c.compte} · ${c.libelle || ''}`} sous={d.ligne.postes > 1 ? c.ligne : null} a={c.a} b={c.b} prov={c.aProv}
                      actif={selC === c.compte} onClick={() => setSelC(selC === c.compte ? null : c.compte)}
                      titre={selC === c.compte ? 'Retirer le filtre' : `Filtrer les ${ONGLET_TIERS.toLowerCase().replace('par ', '')}s et les écritures sur ce compte`} />
                  ))}</tbody>
                  <Pied rows={comptes} />
                </table>
              )}
              {onglet === 'tiers' && (
                <table className="vt-tbl tbl-dense pl-m-tbl">
                  <Tete premiere={NOM_TIERS} />
                  <tbody>{tiers.map((t) => (
                    <Ligne key={t.cle} lib={t.nom} sous={t.code && !t.particuliers ? t.code : null} a={t.a} b={t.b} prov={t.aProv}
                      actif={selT === t.cle} onClick={() => setSelT(selT === t.cle ? null : t.cle)}
                      titre={selT === t.cle ? 'Retirer le filtre' : 'Filtrer les comptes et les écritures sur ce tiers'} />
                  ))}</tbody>
                  <Pied rows={tiers} />
                </table>
              )}
              {onglet === 'concessions' && (
                <table className="vt-tbl tbl-dense pl-m-tbl">
                  <Tete premiere="Concession" />
                  <tbody>{concs.map((x) => (
                    <Ligne key={x.s} lib={x.s} a={x.a} b={x.b} prov={x.aProv}
                      actif={selS === x.s} onClick={() => setSelS(selS === x.s ? null : x.s)}
                      titre={selS === x.s ? 'Retirer le filtre' : 'Filtrer les comptes, les tiers, les marques et les écritures sur cette concession'} />
                  ))}</tbody>
                  <Pied rows={concs} />
                </table>
              )}
              {onglet === 'marques' && (
                <table className="vt-tbl tbl-dense pl-m-tbl">
                  <Tete premiere="Marque" />
                  <tbody>{marques.map((x) => (
                    <Ligne key={x.m} lib={x.nom} a={x.a} b={x.b} prov={x.aProv}
                      actif={selM === x.m} onClick={() => setSelM(selM === x.m ? null : x.m)}
                      titre={selM === x.m ? 'Retirer le filtre' : 'Filtrer les comptes, les tiers et les écritures sur cette marque'} />
                  ))}</tbody>
                  <Pied rows={marques} />
                </table>
              )}
              {onglet === 'ecr' && (
                <>
                  <table className={`vt-tbl tbl-dense pl-m-tbl${ecr?.charge ? ' pl-m-charge' : ''}`}>
                    <thead><tr><th className="l">Date</th><th className="l">Société</th><th className="l">Pièce</th><th className="l">Compte</th>
                      <th className="l">{NOM_TIERS}</th><th className="l">Section</th><th>Montant</th></tr></thead>
                    <tbody>{ecritures.map((x, i) => (
                      <tr key={i}>
                        <td className="l">{x.date ? x.date.split('-').reverse().join('/') : moisLong(x.ym)}</td>
                        <td className="l">{x.societe}</td>
                        <td className="l pl-m-code">{x.piece || '—'}</td>
                        <td className="l" title={x.libelle_compte || ''}>{x.compte} <small className="pl-m-sous pl-m-lib">{x.libelle_compte}</small></td>
                        <td className="l">{x.tiers || '—'}</td>
                        <td className="l" title={x.section || ''}><span className="pl-m-lib">{x.section || '—'}</span></td>
                        <td className="pl-n">{x.provision && !contexte.ecritures ? <span className="pl-m-tag" title="Provision de fin de mois (lot Simulation, journal 9600)">provision</span> : null}{eur(x.montant)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                  <p className="pl-m-note">{d.a.label}{filtre ? ' · filtrées' : ''} · {ecritures.length < (ecr?.nb || 0) ? `${ecritures.length} écritures affichées sur ${ecr.nb} (les plus fortes en valeur absolue)` : `${ecritures.length} écriture${ecritures.length > 1 ? 's' : ''}`}</p>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Pl() { return <CompteResultat modele="pl" />; }
export function Sig({ branche }) { return <CompteResultat modele="sig" branche={branche} />; }

// `branche` : périmètre lu (SIG Auto Métropole, 2026-10-29) ; absent = Antilles.
function CompteResultat({ modele, branche }) {
  const M = MODELES[modele]; const { api } = M;
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [ouvert, toggle] = usePanneau();
  const [replies, setReplies] = useState(() => new Set());
  const [cible, setCible] = useState(null);
  const [charge, setCharge] = useState(false);  // rechargement en cours : l'écran s'estompe
  const societesDispo = useMemo(() => data?.societesDispo || [], [data]);
  const f = useFiltresVentes(societesDispo);
  const mois = f.params.get('mois') || '';
  // Trois lectures (2026-10-29, demande du user) : Mensuel, Par concession
  // (ouverture), Par marque. « Mois » et « Cumul » retirés : le sélecteur
  // N / vs N-1 / vs Budget les couvre. Une ancienne adresse (vue=mois|cumul)
  // retombe sur Par concession.
  // « Par concession groupée » (vue=concessions, ouverture) et « Par
  // concession » (vue=societes, une colonne par société) : 2026-10-29, le user
  // appelle « concession » la société et « concession groupée » le groupe.
  const vue = ['mensuel', 'societes', 'marques'].includes(f.params.get('vue')) && M.lectures.includes(f.params.get('vue')) ? f.params.get('vue') : 'concessions';
  const activite = M.activites && ACT_LIB[f.params.get('act') || ''] ? (f.params.get('act') || '') : '';
  // Comparaison de la vue par concession (2026-10-29) : N (montants), vs N-1
  // ou vs Budget (écarts seuls). Réglage d'affichage, porté par l'URL comme la lecture.
  const cmpUrl = ['n1', 'budget'].includes(f.params.get('cmp')) ? f.params.get('cmp') : '';
  // « Provisions seules » (2026-10-30, demande du user) : un FILTRE (il retire
  // des écritures), donc dans le panneau et dans l'URL ; une pastille dans
  // l'en-tête le rappelle, pour ne jamais prendre ce tableau pour le P&L complet.
  const ecritures = f.params.get('ecr') === 'provisions' ? 'provisions' : '';

  useEffect(() => {
    let actif = true; setErreur(''); setCharge(true);
    api.charger({ mois: mois || undefined, vue, activite: activite || undefined, societes: f.societesUrl || undefined, ecritures: ecritures || undefined, branche })
      .then((r) => { if (actif) setData(r.data); })
      .catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); })
      .finally(() => { if (actif) setCharge(false); });
    return () => { actif = false; };
  }, [api, mois, vue, activite, f.societesUrl, ecritures, branche]);

  const feuilles = useMemo(() => societesDispo.map((s) => ({ ...s, libelle: s.nom })), [societesDispo]);
  // Sans bloc Sociétés dans le panneau (2026-10-29), une société seule doit
  // rester cochable : elle forme sa propre concession groupée, comme au calcul.
  const groupesPanneau = useMemo(() => sectionsGroupes(feuilles.map((x) => ({ ...x, master: x.master || x.nom }))), [feuilles]);
  const moisDispo = data?.moisDispo || [];
  const contexte = useMemo(() => ({ mois: data?.mois, vue, activite: activite || undefined, societes: f.societesUrl || undefined, ecritures: ecritures || undefined, branche }),
    [data?.mois, vue, activite, f.societesUrl, ecritures, branche]);
  const Panneau = (
    <FiltresPanel
      ouvert={ouvert} onToggle={toggle}
      periodes={moisDispo.slice(0, 6).map((m) => ({ code: m, label: moisLong(m) }))}
      periode={data?.mois || mois} onPeriode={(m) => f.setFiltre({ mois: m === moisDispo[0] ? '' : m })}
      moisAutres={moisDispo.slice(6).map((m) => ({ code: m, label: moisLong(m) }))}
      groupes={groupesPanneau} sansSocietes
      choix={{ titre: 'Écritures', valeurs: [{ code: '', nom: 'Toutes' }, { code: 'provisions', nom: 'Provisions seules' }], valeur: ecritures, onChoisir: (c) => f.setFiltre({ ecr: c }) }}
      onToggleGroupe={(g) => f.setFiltre({ societes: basculerGroupe(g, f.selected, societesDispo) })}
      societesDispo={societesDispo} societesActives={f.selected}
      onToggleSociete={f.toggleSociete}
      onToutesSocietes={() => f.setFiltre({ societes: '' })}
      onAucuneSociete={() => f.setFiltre({ societes: '__aucune__' })}
      couleurDe={f.couleurDe}
      nbActifs={(mois ? 1 : 0) + (f.societesUrl ? 1 : 0) + (ecritures ? 1 : 0)}
      onReset={() => f.setFiltre({ societes: '', mois: '', ecr: '' })}
    />
  );

  if (erreur) return <><main className="vt-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>{Panneau}</>;
  if (!data) return <><main className="vt-page"><div className="vt-skel" /></main>{Panneau}</>;

  // Le user raisonne en concession groupée : son nom passe avant celui de la plaque.
  const master = [...new Set(feuilles.filter((x) => f.selected.includes(x.code)).map((x) => x.master))];
  const nomMaster = master.length === 1 && master[0] && feuilles.filter((x) => x.master === master[0]).length === f.selected.length ? master[0] : null;
  const perimetre = nomMaster || nomDuPerimetre(feuilles, f.selected) || (f.selected.length === societesDispo.length ? 'Toutes les sociétés' : `${f.selected.length} sociétés`);
  if (data.indisponible || !data.mois) {
    return <><main className="vt-page"><div className="vt-head"><div><h1>{M.titre}</h1></div></div>
      <div className="bi-empty"><h2>Aucune écriture de compta analytique</h2>
        <p>Le {M.titre} se construit sur la matrice analytique Sage FRP. Importez-la dans A97 DATA (console ▸ Compta analytique{branche === 'auto-metropole' ? ' ▸ Métropole' : ''} ▸ Importer un fichier).</p></div>
    </main>{Panneau}</>;
  }
  if (!f.selected.length) {
    return <><main className="vt-page"><div className="bi-empty"><h2>Aucune société sélectionnée</h2><p>Cochez au moins une société dans les filtres à droite.</p></div></main>{Panneau}</>;
  }
  // Sélection hors du périmètre du P&L (2026-10-28) : le sélecteur de
  // concession de la barre latérale peut transmettre des sociétés d'une autre
  // plaque (ou un code inconnu). Le serveur renvoie alors un P&L sans lignes,
  // et l'écran plantait en lisant le CA d'une ligne absente — écran noir en
  // mode sombre. On le dit, avec le moyen d'en sortir.
  const valides = f.selected.filter((c) => societesDispo.some((x) => x.code === c));
  if (!valides.length || !data.lignes?.length) {
    return <><main className="vt-page"><div className="vt-head"><div><h1>{M.titre}</h1></div></div>
      <div className="bi-empty"><h2>Aucune donnée pour cette sélection</h2>
        <p>{valides.length ? 'Aucune écriture de compta analytique pour ces sociétés sur la période.' : `Les sociétés sélectionnées ne font pas partie du ${M.titre} (sociétés ${branche === 'auto-metropole' ? 'Auto Métropole' : 'Auto Antilles'}).`}</p>
        <p><button type="button" className="pl-lien" onClick={() => f.setFiltre({ societes: '' })}>Afficher toutes les sociétés</button></p></div>
    </main>{Panneau}</>;
  }

  const an = data.mois.slice(0, 4); const an1 = String(Number(an) - 1);
  const L = Object.fromEntries(data.lignes.map((l) => [l.code, l]));
  // Vues en colonnes (mois, ou concessions) : pas de comparaison N-1 / budget,
  // libellé figé à gauche et total figé à droite (classe pl-mensuel).
  const parConc = (vue === 'concessions' || vue === 'societes') && (data.concessions || []).length > 0;
  // Vue par marque (2026-10-29) : une colonne par marque, « Autres marques »,
  // « Commun » (sections sans marque), puis le total du périmètre.
  const parMarq = vue === 'marques' && (data.marques || []).length > 0;
  // La comparaison ne vaut que si la référence est importée (sinon bouton grisé).
  const cmpDispo = { n1: !!data.n1Disponible, budget: !!data.budgetDisponible };
  const parMensuel = vue === 'mensuel' && (data.moisN || []).length > 0;
  const cmp = (parConc || parMensuel) && cmpDispo[cmpUrl] ? cmpUrl : '';
  const refLib = cmp === 'n1' ? an1 : `budget ${an}`;
  // Colonnes comparées : les concessions (cumul) ou les mois. Chacune donne sa
  // valeur, sa référence et la colonne que la fenêtre de détail doit ouvrir.
  const colsCmp = !cmp ? [] : parConc
    ? data.concessions.map((c, i) => ({ cle: c, lib: c, v: (l) => l.conc?.[i], base: (l) => (cmp === 'n1' ? l.concN1 : l.concBudget)?.[i], colonne: cmp === 'n1' ? `conc:${c}` : `concb:${c}` }))
    : data.moisN.map((m, i) => ({ cle: m, lib: moisLong(m), v: (l) => l.mois?.[i], base: (l) => (cmp === 'n1' ? l.moisN1 : l.moisBudget)?.[i], colonne: cmp === 'n1' ? m : `b:${m}` }));
  // Colonnes sans référence (aucune écriture N-1 ou budget) : pas d'écart.
  const sansRef = colsCmp.filter((c) => c.base(L[M.ca] || {}) == null).map((c) => c.lib);
  const ecartComparable = (l) => colsCmp.reduce((t, c) => (c.base(l) == null ? t : t + (c.v(l) || 0) - c.base(l)), 0);
  // Une seule concession : la colonne Total la répéterait.
  const avecTotal = !(parConc && data.concessions.length === 1);
  const mensuel = vue === 'mensuel' || parConc || parMarq;
  const pm = data.partMarque;
  const pctE = (v) => `${fr(Math.round((v || 0) * 100))} %`;
  const budget = data.budgetDisponible && !mensuel;
  const libPeriode = vue === 'mensuel' ? `mensuel ${an}, de janvier à ${MOIS_FR[Number(data.mois.slice(5)) - 1]}${cmp ? ` · écarts vs ${refLib}` : ''}`
    : vue === 'concessions' || vue === 'societes' ? `par concession${vue === 'concessions' ? ' groupée' : ''}, cumul à fin ${moisLong(data.mois)}${cmp ? ` · écarts vs ${refLib}` : ''}`
      : vue === 'marques' ? `par marque, cumul à fin ${moisLong(data.mois)}${pm?.ca != null ? ` · rattaché à une marque : CA ${pctE(pm.ca)}, charges ${pctE(pm.charges)}` : ''}`
        : `cumul à fin ${moisLong(data.mois)}`;
  // Lignes masquées : postes des sous-totaux repliés.
  const cachees = new Set(data.lignes.filter((l) => l.sous && replies.has(l.code)).flatMap((l) => l.plus || []));
  // Provisions seules : pas de taux (une provision rapportée à un CA lui-même
  // provisionné donnait 53 961 % de marge commerciale).
  if (ecritures) data.lignes.filter((l) => l.type === 'taux').forEach((l) => cachees.add(l.code));
  // Ordre d'affichage (demande du user, 2026-10-28) : un sous-total s'affiche
  // AU-DESSUS de ses lignes détaillées (Frais de personnel, puis Salaires et
  // Intéressement), plus naturel quand on déplie — l'Excel le met dessous.
  const parentDe = new Map(data.lignes.filter((l) => l.sous).flatMap((l) => (l.plus || []).map((c) => [c, l])));
  const ordre = [];
  data.lignes.forEach((l) => {
    if (l.sous && ordre.includes(l)) return;
    const p = parentDe.get(l.code);
    if (p && !ordre.includes(p)) ordre.push(p);
    if (!ordre.includes(l)) ordre.push(l);
  });
  const basculer = (code) => setReplies((s) => { const x = new Set(s); x.has(code) ? x.delete(code) : x.add(code); return x; });
  const tousSous = data.lignes.filter((l) => l.sous).map((l) => l.code);
  const toutReplie = tousSous.every((c) => replies.has(c));

  // La modale compare toujours une cellule à sa référence : un clic sur le
  // réalisé N-1 ouvre la même comparaison que le réalisé N (N face à N-1).
  // Colonne d'une marque (« mq:clé ») : cumul N restreint à ses marques.
  const colMq = (colonne) => (colonne.startsWith('mq:') ? (data.marques || []).find((c) => c.cle === colonne.slice(3)) : null);
  const ouvrir = (l, colonne, periode) => {
    const mq = colMq(colonne);
    setCible({ ligne: l.code, libelle: l.libelle, colonne: colonne === 'n1' ? 'n' : colonne,
      periode: colonne === 'n1' ? libCol('n') : periode,
      perimetre: colonne.startsWith('conc:') ? colonne.slice(5) : colonne.startsWith('concb:') ? colonne.slice(6) : mq ? `${perimetre} · ${mq.commun ? 'Commun (sans marque)' : mq.vo ? 'VO, toutes marques' : mq.libelle}` : perimetre });
  };
  const libCol = (colonne) => (colonne === 'n1' ? `cumul à fin ${moisLong(`${an1}${data.mois.slice(4)}`)}`
    : colonne === 'budget' ? `budget à fin ${moisLong(data.mois)}`
      : /^b:\d{4}-\d{2}$/.test(colonne) ? moisLong(colonne.slice(2))
      : /^\d{4}-\d{2}$/.test(colonne) ? moisLong(colonne)
        : `cumul à fin ${moisLong(data.mois)}`);
  // Cellule montant : cliquable quand la ligne se décompose en écritures.
  const Cel = ({ l, v, colonne, className = '', children }) => (l.detail
    ? <td className={`${className} pl-clic`} onClick={() => ouvrir(l, colonne, libCol(colonne))} title="Voir le détail">{children ?? valeur(l, v)}</td>
    : <td className={className}>{children ?? valeur(l, v)}</td>);
  const Ecarts = ({ l, refCle }) => {
    if (l.type === 'taux') {
      const e = ecart(l.n, l[refCle]);
      return <><td className={cl(e)}>{e == null ? '—' : l.ratio ? ratio(e) : `${fr(arr(e * 100, 1), { signDisplay: 'exceptZero', minimumFractionDigits: 1, maximumFractionDigits: 1 })} pt`}</td><td /></>;
    }
    const e = ecart(l.n, l[refCle]); const p = varPct(l.n, l[refCle]);
    return <>
      <Cel l={l} colonne={refCle === 'budget' ? 'budget' : 'n'} className={cl(e)}>{k1s(e)}</Cel>
      <td className={cl(p)}>{pctS(p)}</td>
    </>;
  };

  // Cellule d'écart de la vue par concession : vert = favorable sur TOUTES les
  // lignes (produits positifs, charges négatives : un écart positif est
  // toujours une bonne nouvelle). Taux en points, montants en k€ signés.
  const CelEcart = ({ l, v, base, colonne, className = '' }) => {
    const e = ecart(v, base);
    const txt = e == null ? '—' : l.type === 'taux'
      ? (l.ratio ? ratio(e) : `${fr(arr(e * 100, 1), { signDisplay: 'exceptZero', minimumFractionDigits: 1, maximumFractionDigits: 1 })} pt`) : k1s(e);
    const p = l.type === 'taux' ? null : varPct(v, base);
    // Couleur sur la valeur AFFICHÉE : un « 0 » (−300 €) ne se colore pas.
    const ce = e == null ? '' : cl(l.type === 'taux' ? arr(e * 100, 1) : arr(e / 1000));
    const titre = e == null ? `Pas de ${cmp === 'n1' ? `réalisé ${an1}` : 'budget'} pour ${parConc ? 'cette concession' : 'ce mois'}`
      : `${an} : ${valeur(l, v)} · ${refLib} : ${valeur(l, base)}${p != null ? ` · ${pctS(p)}` : ''}${l.detail ? ' — clic : détail' : ''}`;
    return l.detail && e != null
      ? <td className={`${className} ${ce} pl-clic`} title={titre} onClick={() => ouvrir(l, colonne, libCol(colonne))}>{txt}</td>
      : <td className={`${className} ${ce}`} title={titre}>{txt}</td>;
  };

  const kpi = (code, tauxCode, titre) => {
    const l = L[code]; const t = tauxCode && !ecritures ? L[tauxCode] : null; const p = varPct(l.n, l.n1);
    return (
      <button type="button" className="vt-kpi pl-kpi" key={code} onClick={() => ouvrir(l, 'n', libCol('n'))} title="Voir le détail">
        <div className="l">{titre || l.libelle}</div>
        <div className="v">{k1(l.n)} k€</div>
        <div className="d"><small>
          {t ? `${pctV(t.n)} du CA · ` : ''}
          {l.n1 == null ? 'N-1 non importé' : <>N-1 {k1(l.n1)} k€ · <span className={cl(p)}>{pctS(p)}</span></>}
        </small></div>
      </button>
    );
  };
  const na = data.nonAffecte;

  return (
    <>
      <main className={`vt-page${charge ? ' pl-charge' : ''}`} aria-busy={charge}>
        <div className="vt-head">
          <div><h1>{M.titre}{activite ? ` — ${ACT_LIB[activite]}` : ''}
            {ecritures && <button type="button" className="pl-pastille" onClick={() => f.setFiltre({ ecr: '' })} title="Retirer le filtre : revenir à toutes les écritures">Provisions seules ✕</button>}</h1></div>
          <div className="vt-asof"><b>{perimetre}</b><br /><span>{libPeriode}</span>
            {data.controle && Math.abs(data.controle.ecart) < 5000 && (
              <><br /><span title="Résultat avant impôt des SIG (RCAI + exceptionnel + participation) face au RCAI du P&L, même périmètre, même cumul. L'écart est le solde des flux internes de classe 9.">
                rapproché du P&L : écart {k1s(data.controle.ecart)} k€</span></>
            )}
          </div>
        </div>

        {/* Contrôle des SIG (2026-10-29) : résultat avant impôt = RCAI du P&L, même périmètre. Au-delà de 5 k€
            (le solde des flux internes de classe 9 fait ≈ 1 k€), une écriture tombe dans l'un et pas dans l'autre. */}
        {data.controle && Math.abs(data.controle.ecart) >= 5000 && (
          <div className="pl-alerte">⚠️ Résultat avant impôt des SIG ({k1(data.controle.sig)} k€) différent du RCAI du P&L ({k1(data.controle.pl)} k€) : écart {k1s(data.controle.ecart)} k€ à expliquer.</div>
        )}
        {/* Métropole (2026-10-29) : stock d'ouverture passé en charge en janvier, stock final constaté aux
            seules situations, extournées le mois suivant. Un mois isolé est donc faux, le cumul à une situation juste. */}
        {branche === 'auto-metropole' && vue === 'mensuel' && (
          <div className="hint pl-hint">Auto Métropole : le stock n'est constaté qu'aux situations (juin, septembre…), extournées le mois suivant, et le stock d'ouverture passe en charge en janvier. Un mois isolé est donc faussé ; seul le cumul à fin de situation se lit.</div>
        )}
        {ecritures && (
          <div className="hint pl-hint">{branche === 'auto-metropole'
            ? 'Provisions seules : les écritures de situation (lots Simulation, journal 9090), stock constaté compris, extournées le mois suivant. Cliquez sur un montant pour voir les écritures.'
            : 'Provisions seules : les provisions de fin de mois (lots Simulation, journal 9600), sans les écritures comptabilisées. Le budget ne les distingue pas, d’où l’absence de comparaison au budget. Cliquez sur un montant pour voir les écritures.'}</div>
        )}
        {cmp && sansRef.length > 0 && (
          <div className="pl-alerte">⚠️ Pas de {refLib} pour {sansRef.join(', ')} : pas d'écart {parConc ? `sur ${sansRef.length > 1 ? 'ces concessions' : 'cette concession'}` : `sur ${sansRef.length > 1 ? 'ces mois' : 'ce mois'}`}, et le total ne compte que {parConc ? 'les concessions' : 'les mois'} comparables.</div>
        )}
        {/* Périmètres N / N-1 différents : utile aussi en mensuel « vs N-1 » (chaque mois a bien une référence,
            mais sans les mêmes sociétés). Par concession, la concession sans N-1 a déjà son « — » et son bandeau. */}
        {data.n1Disponible && (data.couverture?.absentesN1?.length > 0 || data.couverture?.absentesN?.length > 0) && (!mensuel || (parMensuel && cmp === 'n1')) && (
          <div className="pl-alerte">⚠️ Comparaison {an} / {an1} sur des périmètres différents :
            {data.couverture.absentesN1.length > 0 && <> {data.couverture.absentesN1.join(', ')} sans écritures en {an1}.</>}
            {data.couverture.absentesN.length > 0 && <> {data.couverture.absentesN.join(', ')} sans écritures en {an}.</>}
            {' '}Les écarts N-1 ne sont pas comparables : choisissez des sociétés importées sur les deux années.</div>
        )}
        <div className="vt-kpis pl-kpis" style={{ gridTemplateColumns: 'repeat(4,1fr)' }}>
          {M.kpis.map(([code, taux, titre]) => kpi(code, taux, titre))}
        </div>

        <div className="vt-card">
          <div className="vt-card-h pl-card-h">
            {/* Titre et ligne d'aide retirés (demande du user, 2026-10-28) : « Tout
                replier » prend la place du titre, à gauche. */}
            {/* À gauche : replier, et la comparaison de la vue par concession (la colonne de gauche est la seule à avoir la place). */}
            <div className="pl-gauche">
              <button type="button" className="pl-lien pl-replier" onClick={() => setReplies(toutReplie ? new Set() : new Set(tousSous))}>{toutReplie ? 'Tout déplier' : 'Tout replier'}</button>
              {(vue === 'concessions' || vue === 'societes' || vue === 'mensuel') && (
                <span className="st-seg" aria-label="Comparaison">
                  <button type="button" className={!cmp ? 'on' : ''} onClick={() => f.setFiltre({ cmp: '' })}>N</button>
                  <button type="button" className={cmp === 'n1' ? 'on' : ''} disabled={!cmpDispo.n1} onClick={() => f.setFiltre({ cmp: 'n1' })}
                    title={cmpDispo.n1 ? `Écarts ${an} − ${an1}, cumul à fin ${moisLong(data.mois)}` : `${an1} n'est pas importé dans A97 DATA (compta analytique)`}>vs N-1</button>
                  <button type="button" className={cmp === 'budget' ? 'on' : ''} disabled={!cmpDispo.budget} onClick={() => f.setFiltre({ cmp: 'budget' })}
                    title={cmpDispo.budget ? `Écarts réalisé − budget ${an}` : ecritures ? 'Le budget ne distingue pas les provisions' : `Aucun budget ${an} importé dans A97 DATA (compta analytique)`}>vs Budget</button>
                </span>
              )}
            </div>
            {/* Sélecteur d'activité centré dans l'en-tête (demande du user, 2026-10-28). */}
            {M.activites ? (
              <span className="st-seg pl-activites" aria-label="Activité">
                {Object.entries(ACT_LIB).map(([c, lib]) => (
                  <button key={c || 'tot'} type="button" className={activite === c ? 'on' : ''} onClick={() => f.setFiltre({ act: c })}>{lib}</button>
                ))}
              </span>
            ) : <span />}
            <div className="st-tools">
              <span className="st-seg" aria-label="Lecture">
                <button type="button" className={vue === 'mensuel' ? 'on' : ''} onClick={() => f.setFiltre({ vue: 'mensuel' })}
                  title={`Un mois par colonne, de janvier à ${moisLong(data.mois)}, et le cumul`}>Mensuel {an}</button>
                <button type="button" className={vue === 'concessions' ? 'on' : ''} onClick={() => f.setFiltre({ vue: '' })}
                  title={`Les concessions groupées côte à côte, cumul à fin ${moisLong(data.mois)}`}>Par concession groupée</button>
                <button type="button" className={vue === 'societes' ? 'on' : ''} onClick={() => f.setFiltre({ vue: 'societes' })}
                  title={`Chaque concession (société) côte à côte, cumul à fin ${moisLong(data.mois)}`}>Par concession</button>
                {M.lectures.includes('marques') && (
                  <button type="button" className={vue === 'marques' ? 'on' : ''} onClick={() => f.setFiltre({ vue: 'marques' })}
                    title={`Les marques côte à côte, cumul à fin ${moisLong(data.mois)}, et ce qui n'est rattaché à aucune marque`}>Par marque</button>
                )}
              </span>
            </div>
          </div>
          <div className="vt-card-b vt-tbl-wrap">
            <table className={`vt-tbl tbl-dense pl-tbl${mensuel ? ' pl-mensuel' : ''}`}>
              <thead><tr>
                <th className="l">En k€</th>
                {parConc ? <>
                  {data.concessions.map((c) => <th key={c} className="pl-conc">{c}</th>)}
                  {avecTotal && <th className="pl-n pl-cumul">Total</th>}
                </> : parMarq ? <>
                  {data.marques.map((c) => (
                    <th key={c.cle} className={`pl-conc${c.vo ? ' pl-commun' : ''}`}
                      title={c.commun ? 'Sections sans marque : administratif, frais communs VN, atelier, magasin…'
                        : c.vo ? 'Le VO n’est pas ventilé par marque : ses ventes portent la marque du véhicule vendu, ses achats aucune'
                          : c.membres ? c.membres.join(', ') : undefined}>{c.libelle}</th>
                  ))}
                  <th className="pl-n pl-cumul">Total</th>
                </> : mensuel ? <>
                  {data.moisN.map((m) => <th key={m}>{moisCourt(m)}</th>)}
                  <th className="pl-n pl-cumul">Cumul {an}</th>
                </> : <>
                  <th>Réalisé {an1}</th>
                  {budget && <th>Budget {an}</th>}
                  <th className="pl-n">Réalisé {an}</th>
                  <th>Écart N-1</th><th>%</th>
                  {budget && <><th>Écart budget</th><th>%</th></>}
                </>}
              </tr></thead>
              <tbody>
                {ordre.filter((l) => !cachees.has(l.code)).map((l) => (
                  <tr key={l.code} className={[M.totaux.has(l.code) ? 'pl-tot' : '', l.sous ? 'pl-sous' : '', l.type === 'taux' ? 'pl-taux' : '', l.niveau ? 'pl-det' : ''].filter(Boolean).join(' ')}>
                    <td className="l">
                      {l.sous ? (
                        <button type="button" className={`rg-btn${replies.has(l.code) ? '' : ' on'}`} onClick={() => basculer(l.code)}
                          title={replies.has(l.code) ? 'Déplier le détail' : 'Replier le détail'} aria-label="Replier ou déplier">▶</button>
                      ) : !l.niveau ? <span className="pl-btn-vide" aria-hidden="true" /> : null}
                      {l.libelle}
                      {l.type === 'saisie' ? <span className="pl-note" title="Saisis à la main dans le P&L Excel : aucune source dans la compta analytique">non saisis</span> : null}
                    </td>
                    {cmp ? <>
                      {colsCmp.map((c) => <CelEcart key={c.cle} l={l} v={c.v(l)} base={c.base(l)} colonne={c.colonne} />)}
                      {!avecTotal ? null : sansRef.length ? (
                        // Une colonne sans référence : le total ne compte que les colonnes comparables
                        // (3 concessions face à 2 donnaient +66 % de CA). Pas de taux recalculable ici.
                        <td className={`pl-n pl-cumul ${l.type === 'taux' ? '' : cl(arr(ecartComparable(l) / 1000))}`} title={`Hors ${sansRef.join(', ')} (pas de ${refLib})`}>
                          {l.type === 'taux' ? '—' : k1s(ecartComparable(l))}</td>
                      ) : <CelEcart l={l} v={l.n} base={cmp === 'n1' ? l.n1 : l.budget} colonne={cmp === 'n1' ? 'n' : 'nb'} className="pl-n pl-cumul" />}
                    </> : parConc ? <>
                      {data.concessions.map((c, i) => <Cel key={c} l={l} v={l.conc?.[i]} colonne={`conc:${c}`} />)}
                      {avecTotal && <Cel l={l} v={l.n} colonne="n" className="pl-n pl-cumul" />}
                    </> : parMarq ? <>
                      {/* Pas de taux sur Commun ni sur une colonne sans CA positif (−99 % de marge sur 0,4 M€ de CA
                          résiduel ne dit rien), ni de ratio CA / masse salariale : le personnel est presque tout en Commun. */}
                      {data.marques.map((c, i) => <Cel key={c.cle} l={l} v={l.type === 'taux' && (l.ratio || c.commun || !(L.ca_ht.marq?.[i] > 0)) ? null : l.marq?.[i]}
                        colonne={`mq:${c.cle}`} className={c.vo ? 'pl-commun' : ''} />)}
                      <Cel l={l} v={l.n} colonne="n" className="pl-n pl-cumul" />
                    </> : mensuel ? <>
                      {data.moisN.map((m, i) => <Cel key={m} l={l} v={l.mois?.[i]} colonne={m} />)}
                      <Cel l={l} v={l.n} colonne="n" className="pl-n pl-cumul" />
                    </> : <>
                      <Cel l={l} v={l.n1} colonne="n1" />
                      {budget && <Cel l={l} v={l.budget} colonne="budget" />}
                      <Cel l={l} v={l.n} colonne="n" className="pl-n" />
                      <Ecarts l={l} refCle="n1" />
                      {budget && <Ecarts l={l} refCle="budget" />}
                    </>}
                  </tr>
                ))}
                {na && (Math.abs(na.n) >= 0.5 || Math.abs(na.n1 || 0) >= 0.5) && (
                  <tr className="pl-na" title="Comptes de classe 6 ou 7 qu'aucune règle d'affectation ne prend : ils ne sont dans aucun total.">
                    <td className="l">⚠️ Écritures non affectées <small>({na.comptes} compte{na.comptes > 1 ? 's' : ''}, hors totaux)</small></td>
                    {mensuel ? <>{(parConc ? data.concessions : parMarq ? data.marques.map((c) => c.cle) : data.moisN).map((m) => <td key={m} />)}{avecTotal && <td className="pl-n pl-cumul">{cmp ? '' : k1(na.n)}</td>}</>
                      : <><td>{k1(na.n1)}</td>{budget && <td />}<td className="pl-n">{k1(na.n)}</td><td /><td />{budget && <><td /><td /></>}</>}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
      {Panneau}
      {cible && <ModaleDetail cible={cible} contexte={contexte} api={api} onFermer={() => setCible(null)} />}
    </>
  );
}
